import { Image, Pressable, View } from "react-native";
import { Check } from "lucide-react-native";
import {
  CARD_RADIUS,
  cardSurface,
  PrimaryTitleText,
  Row,
  Tag,
  type TagTone,
} from "@louise/moon-ui";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  DOSE_STATUS_COLORS,
  DOSE_STATUS_ICONS,
  extractMedicationShortName,
  type DoseStatus,
} from "@src/packages/care/medications";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useMedicationFormCorrection } from "@src/packages/care/useMedicationFormCorrection";
import { resolveCoachProtocols } from "@src/packages/injection-coach/availability";
import { useOpenedVialEvidence, vialEvidenceMatters } from "@src/packages/care";
import { InjectionCoachEntryButton } from "@src/packages/injection-coach/components/InjectionCoachEntryButton";
import { PartnerAvatarTag } from "./PartnerAvatarTag";
import type { ProtocolDoseUnit } from "@src/packages/injection-protocol/types";

export type MedicationForm =
  "pen" | "vial" | "pill" | "suppository" | "supplies";
export type MedicationRowSurface = "standalone" | "nested";
export type MedicationRowAppearance = "default" | "calendar-glass";
export type MedicationRowRightAction =
  "checkbox" | "chevron" | "partnerTag" | "none";
export type MedicationAvailabilityStatus =
  | "on-hand"
  | "in-transit"
  | "pickup-ready"
  | "low-stock"
  | "not-on-hand"
  | "refill-needed"
  | "ordered"
  | "unknown";

const medicationPillIllustration =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;
const medicationVialIllustration =
  require("@src/apps/moon/assets/images/vial-syringe.png") as number;
const medicationPenIllustration =
  require("@src/apps/moon/assets/images/injection-pen.png") as number;

function inferMedicationFormFromFormat(format: string | undefined) {
  const normalized = format?.toLowerCase() ?? "";
  if (normalized.includes("pen")) return "pen";
  if (normalized.includes("vial")) return "vial";
  if (normalized.includes("oral") || normalized.includes("pill")) return "pill";
  return null;
}

function getMedicationIllustration(
  form: MedicationForm | undefined,
  format: string | undefined,
) {
  const resolvedForm = form ?? inferMedicationFormFromFormat(format);

  if (resolvedForm === "pen") return medicationPenIllustration;
  if (resolvedForm === "vial") return medicationVialIllustration;
  return medicationPillIllustration;
}

function buildMedicationSubtitle(format: string | undefined, subtitle: string) {
  if (!format) return subtitle;
  if (!subtitle) return format;
  return `${format} · ${subtitle}`;
}

function getAvailabilityTag(
  status: MedicationAvailabilityStatus | undefined,
): { labelKey: string; tone: TagTone } | null {
  if (!status || status === "on-hand") {
    return null;
  }
  if (status === "in-transit") {
    return { labelKey: "homepage.medication_row.in_transit", tone: "gold" };
  }
  if (status === "pickup-ready") {
    return {
      labelKey: "homepage.medication_row.pickup_ready",
      tone: "neutral",
    };
  }
  if (status === "low-stock" || status === "refill-needed") {
    return { labelKey: "homepage.medication_row.low_stock", tone: "orange" };
  }
  if (status === "not-on-hand") {
    return { labelKey: "homepage.medication_row.not_on_hand", tone: "coral" };
  }
  if (status === "ordered") {
    return { labelKey: "homepage.medication_row.ordered", tone: "neutral" };
  }
  return null;
}

export function MedicationRow({
  name,
  dose,
  format,
  subtitle,
  form,
  availabilityStatus,
  leftLabel,
  time,
  surface = "standalone",
  appearance = "default",
  rightAction = "none",
  completed = false,
  doseStatus,
  isStopped = false,
  partnerName,
  routes,
  value,
  apiForm,
  patientMedicationId,
  onPress,
  onCheckboxPress,
  onCorrectDose,
}: {
  name: string;
  dose?: string;
  format?: string;
  subtitle: string;
  form?: MedicationForm;
  /**
   * `medication.form` as the API stores it — **not** `form` above.
   *
   * `form` has four values and exists to choose an illustration:
   * `pre_filled_syringe`, `injection_solution` and `lyophilized_powder` all
   * collapse into `"vial"`. The injection coach cannot read an answer out of
   * that, so it went on asking her which device she has on every row on this
   * screen — and told her, wrongly, that her calendar had no record of it.
   */
  apiForm?: string | null;
  /**
   * The scheduled dose this row is for, so a device correction can be stored.
   *
   * Absent means the correction applies to this walk only, which is the honest
   * state for a row with no record behind it.
   */
  patientMedicationId?: string | null;
  availabilityStatus?: MedicationAvailabilityStatus;
  leftLabel?: string;
  time?: string;
  surface?: MedicationRowSurface;
  appearance?: MedicationRowAppearance;
  rightAction?: MedicationRowRightAction;
  completed?: boolean;
  doseStatus?: DoseStatus;
  isStopped?: boolean;
  partnerName?: string;
  /** A value at the row's end, such as a price on a pharmacy price list. */
  value?: string;
  /**
   * Every route recorded for the medication, for the injection coach shortcut.
   *
   * Absent on the surfaces that render this row without a schedule behind it —
   * a pharmacy card, the factsheet's own summary — and the shortcut simply does
   * not appear there.
   */
  routes?: readonly (string | null | undefined)[];
  onPress?: () => void;
  onCheckboxPress?: () => void;
  /** Where a dose she confirms against her box is written back; see the coach. */
  onCorrectDose?: (dose: { value: number; unit: ProtocolDoseUnit }) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicationRow");
  const subtitleText = buildMedicationSubtitle(format, subtitle);
  const illustration = getMedicationIllustration(form, format);
  const isStandalone = surface === "standalone";
  const isCalendarGlass = appearance === "calendar-glass";
  const availabilityTag = getAvailabilityTag(availabilityStatus);
  const leadingLabel = leftLabel ?? time;
  const titleRight =
    dose || availabilityTag ? (
      <>
        {dose ? (
          <Tag label={dose} tone={isCalendarGlass ? "onDark" : "neutral"} />
        ) : null}
        {availabilityTag ? (
          <Tag
            label={t(availabilityTag.labelKey)}
            // The calendar's dark glass reads one light tone for every status.
            tone={isCalendarGlass ? "onDark" : availabilityTag.tone}
          />
        ) : null}
      </>
    ) : null;

  // Hooks cannot be called conditionally, so this exists whether or not the
  // shortcut renders. It does nothing until the coach reports a correction.
  const correctMedicationForm = useMedicationFormCorrection(
    patientMedicationId,
    apiForm,
  );
  // Whether her vial is already mixed, for the one medication where that
  // changes the procedure. Read here, passed down: the coach never fetches.
  const hasEarlierDose = useOpenedVialEvidence(patientMedicationId, {
    // Only where the answer can change a walk. One medication qualifies today,
    // and without this the read ran once per medication row on screen.
    enabled: vialEvidenceMatters(resolveCoachProtocols(name)?.protocols ?? []),
  });

  // Straight to the steps, without opening the factsheet first. A patient at the
  // fridge with a needle in her other hand should not have to read a factsheet
  // to reach them. It renders itself only where an approved procedure exists and
  // an escalation is in reach, so there is nothing to gate here.
  const coachShortcut =
    routes && !completed ? (
      <InjectionCoachEntryButton
        variant="icon"
        medicationName={name}
        routes={routes}
        prescribedDose={dose}
        apiForm={apiForm}
        patientMedicationId={patientMedicationId}
        onFormCorrected={correctMedicationForm}
        hasEarlierDose={hasEarlierDose}
        // Straight from the last step into the dose logger, so she is not made
        // to finish the walk and then separately declare she took it. Offered
        // only where the checkbox is: a row that cannot be logged must not end
        // in a dialog asking her to log it.
        onCoachFinished={
          rightAction === "checkbox" ? onCheckboxPress : undefined
        }
        onCorrectDose={onCorrectDose}
      />
    ) : null;

  const rightControl = value ? (
    <PrimaryTitleText style={{ fontWeight: "700", flexShrink: 0 }}>
      {value}
    </PrimaryTitleText>
  ) : rightAction === "partnerTag" && partnerName ? (
    <View
      style={{
        width: 74,
        alignItems: "flex-end",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <PartnerAvatarTag label={partnerName} />
    </View>
  ) : rightAction === "checkbox" ? (
    (() => {
      const statusColor = doseStatus ? DOSE_STATUS_COLORS[doseStatus] : null;
      const StatusIcon = doseStatus ? DOSE_STATUS_ICONS[doseStatus] : Check;
      const filled = Boolean(doseStatus);
      return (
        <Pressable
          accessibilityRole="checkbox"
          accessibilityLabel={t("homepage.review_concept.log_for_a11y", {
            params: { label: name },
          })}
          accessibilityState={{ checked: filled }}
          onPress={(event) => {
            event.stopPropagation?.();
            capturePress(pressLabel(name), "medication_row_checkbox");
            onCheckboxPress?.();
          }}
          style={{
            width: 34,
            alignItems: "flex-end",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: filled
                ? (statusColor ?? "transparent")
                : "transparent",
              borderWidth: filled ? 0 : 1.5,
              borderColor: filled
                ? (statusColor ?? "transparent")
                : isCalendarGlass
                  ? semanticColors.onDark.text.secondary
                  : semanticColors.border.strong,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {filled ? (
              <StatusIcon
                size={14}
                color={semanticColors.text.inverse}
                strokeWidth={2.5}
              />
            ) : null}
          </View>
        </Pressable>
      );
    })()
  ) : null;

  // Additive, unlike the expression above it: the existing right-hand control is
  // an exclusive choice between a partner tag and a checkbox, and the shortcut
  // sits beside whichever one won rather than replacing it.
  const trailing =
    coachShortcut || rightControl ? (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        {coachShortcut}
        {rightControl}
      </View>
    ) : null;

  return (
    <Row
      accessibilityRole="button"
      surface={isCalendarGlass ? "plain" : isStandalone ? "glass" : "plain"}
      density={isCalendarGlass || isStandalone ? "default" : "dense"}
      subtitleVariant="meta"
      leadingMeta={leadingLabel}
      leadingMetaWidth={50}
      leadingMetaStyle={{
        color:
          isStopped || completed
            ? isCalendarGlass
              ? semanticColors.onDark.text.tertiary
              : semanticColors.text.faint
            : isCalendarGlass
              ? semanticColors.onDark.text.tertiary
              : EMBER.colors.textLight,
        textDecorationLine: isStopped ? "line-through" : "none",
        textDecorationColor: semanticColors.text.faint,
      }}
      leading={
        isCalendarGlass ? undefined : (
          <Image
            source={illustration}
            resizeMode="contain"
            style={{
              width: isStandalone ? 32 : 28,
              height: isStandalone ? 32 : 28,
            }}
          />
        )
      }
      leadingSize={isStandalone ? 34 : 30}
      gap={isCalendarGlass ? 10 : isStandalone ? 12 : 11}
      borderRadius={isStandalone ? CARD_RADIUS : 12}
      paddingHorizontal={isCalendarGlass ? 11 : isStandalone ? 16 : 0}
      paddingVertical={isCalendarGlass ? 10 : isStandalone ? 13 : 9}
      style={
        isCalendarGlass
          ? cardSurface({ variant: "panel", tone: "onDark" })
          : undefined
      }
      muted={completed}
      onPress={onPress}
      action="medication_row"
      // A price list names full products: strength and form are the only
      // things telling two prices apart, so keep the regulatory label here.
      // Elsewhere, short brand names read cleaner in the row.
      title={value ? name : extractMedicationShortName(name) || name}
      // A price list names full products; let them wrap rather than cut.
      titleNumberOfLines={value ? undefined : 1}
      titleRight={titleRight}
      titleStyle={{
        color:
          isStopped || completed
            ? isCalendarGlass
              ? semanticColors.onDark.text.tertiary
              : semanticColors.text.tertiary
            : isCalendarGlass
              ? semanticColors.onDark.text.primary
              : isStandalone
                ? semanticColors.typography.primary
                : EMBER.colors.text,
        textDecorationLine: isStopped ? "line-through" : "none",
        textDecorationColor: semanticColors.text.tertiary,
      }}
      subtitle={subtitleText}
      subtitleNumberOfLines={isStandalone ? undefined : 2}
      subtitleStyle={{
        color:
          isStopped || completed
            ? isCalendarGlass
              ? semanticColors.onDark.text.tertiary
              : semanticColors.text.faint
            : isCalendarGlass
              ? semanticColors.onDark.text.secondary
              : isStandalone
                ? semanticColors.typography.supporting
                : EMBER.colors.textMid,
        textDecorationLine: isStopped ? "line-through" : "none",
        textDecorationColor: semanticColors.text.faint,
      }}
      trailing={trailing}
      chevronColor={
        isCalendarGlass ? semanticColors.onDark.text.secondary : undefined
      }
      chevron={rightAction === "chevron"}
    />
  );
}
